/**
 * Alternative version of `Array.prototype.includes` that allows testing for
 * the existence of an item with a type that is a _superset_ of the type of the
 * items in the array.
 *
 * This allows us to use it to check whether an item of type `string` exists in
 * an array of string literals (e.g: `['foo', 'bar'] as const`) without TypeScript
 * complaining. It will, however, throw a compiler error if you try to pass an item
 * of type `number`.
 *
 * @example
 * const things = ['foo', 'bar'] as const;
 *
 * // error
 * const hasThing = (t: string) => things.includes(t);
 *
 * // compiles
 * const hasThing = (t: string) => includes(things, t);
 *
 * @param array array to search
 * @param item item to search for
 */
var includes = function includes(array, item) {
  return array.includes(item);
};

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = null == arr ? null : "undefined" != typeof Symbol && arr[Symbol.iterator] || arr["@@iterator"];
  if (null != _i) {
    var _s,
      _e,
      _x,
      _r,
      _arr = [],
      _n = !0,
      _d = !1;
    try {
      if (_x = (_i = _i.call(arr)).next, 0 === i) {
        if (Object(_i) !== _i) return;
        _n = !1;
      } else for (; !(_n = (_s = _x.call(_i)).done) && (_arr.push(_s.value), _arr.length !== i); _n = !0);
    } catch (err) {
      _d = !0, _e = err;
    } finally {
      try {
        if (!_n && null != _i.return && (_r = _i.return(), Object(_r) !== _r)) return;
      } finally {
        if (_d) throw _e;
      }
    }
    return _arr;
  }
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

/**
 * Contruct a type by picking the set of
 * properties with values matching `Value`
 */

/**
 * A version of `Object.entries` that preserves
 * narrower types in the resulting tuple entries
 *
 * @example
 * const o = { a: 1, b: 2 };
 *
 * Object.entries(o) // -> [string, number][]
 * entries(o) // -> ['a' | 'b', number][]
 *
 *
 * ⚠️ NOTE: TypeScript cannot guarantee that an object type
 * cannot have additional properties, so this should only
 * be used when you know that won't be the case
 */
var entries = function entries(object) {
  return Object.entries(object);
};

/**
 * A version of `Object.fromEntries` for immutable sets of entries that
 * preserves a narrower type for the keys of the resulting object
 *
 * @example
 * const e = [['a', 1] as const, ['b', 2] as const];
 *
 * Object.fromEntries(e) // -> { [k: string]: number }
 * fromEntries(e) // -> { [k: 'a' | 'b']: number }
 */
var fromEntries = function fromEntries(e) {
  return Object.fromEntries(e);
};

/**
 * Return subset of object including specified keys
 */
var pick = function pick(object, keys) {
  return Object.fromEntries(entries(object).filter(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 1),
      key = _ref2[0];
    return includes(keys, key);
  }));
};

/**
 * Return subset of object excluding specified keys
 */
var omit = function omit(object, keys) {
  return Object.fromEntries(entries(object).filter(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 1),
      key = _ref4[0];
    return !includes(keys, key);
  }));
};

/**
 * Split an object into two with the first including the specified keys and the
 * second including the remaining keys
 */
var split = function split(object, keys) {
  return [pick(object, keys), omit(object, keys)];
};

/**
 * Apply function to all values of an object
 */
var mapValues = function mapValues(object, fn) {
  return fromEntries(entries(object).map(function (_ref5) {
    var _ref6 = _slicedToArray(_ref5, 2),
      key = _ref6[0],
      value = _ref6[1];
    return [key, fn(value)];
  }));
};

export { _arrayWithHoles as _, _unsupportedIterableToArray as a, _nonIterableRest as b, entries as e, fromEntries as f, includes as i, mapValues as m, omit as o, pick as p, split as s };
